
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import SmartEditor from './SmartEditor';
import Toolbar from './Toolbar';
import { formatJson, minifyJson, escapeJson, unescapeJson } from '../utils/jsonUtils';
import { jsonToXml } from '../utils/conversionUtils';
import { useTranslation } from '../hooks/useTranslation';
import { Code2 } from 'lucide-react';
import './JsonWorkspace.css';

const JsonWorkspace = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const routeCode = location.state?.code ?? '';
    const [input, setInput] = useState(routeCode);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);
    const [isEscaped, setIsEscaped] = useState(true);
    const [isMinified, setIsMinified] = useState(false); // 跟踪当前是否为压缩状态
    const [isFolded, setIsFolded] = useState(false); // 跟踪当前是否为折叠状态
    const restoreRef = React.useRef({ original: null, unescaped: null });
    const editorRef = React.useRef(null); // 编辑器引用

    // Clear success message after 3 seconds
    useEffect(() => {
        if (successMsg) {
            const timer = setTimeout(() => setSuccessMsg(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [successMsg]);

    // Auto-format on mount if input comes from router state
    useEffect(() => {
        if (routeCode) {
            try {
                const formatted = formatJson(routeCode);
                setInput(formatted);
                setError(null);
                setSuccessMsg('Auto-formatted');
                setIsMinified(false); // 自动格式化后设置为非压缩状态
                setIsFolded(false);
            } catch (err) {
                setInput(routeCode);
                setError('Auto-format failed: ' + err.message);
            }
        }
    }, [routeCode]);

    const handleFormat = () => {
        if (!input.trim()) return;
        try {
            const formatted = formatJson(input);
            setInput(formatted);
            setError(null);
            setSuccessMsg('Formatted successfully');
            setIsMinified(false); // 格式化后设置为非压缩状态
            setIsFolded(false); // 格式化后重置折叠状态
        } catch (err) {
            setError('Invalid JSON: ' + err.message);
        }
    };

    const handleMinify = () => {
        if (!input.trim()) return;
        try {
            if (isMinified) {
                // 当前是压缩状态，点击后执行格式化
                const formatted = formatJson(input);
                setInput(formatted);
                setError(null);
                setSuccessMsg('Formatted successfully');
                setIsMinified(false);
            } else {
                // 当前是格式化状态，点击后执行压缩
                const minified = minifyJson(input);
                setInput(minified);
                setError(null);
                setSuccessMsg('Minified successfully');
                setIsMinified(true);
            }
        } catch (err) {
            setError('Invalid JSON: ' + err.message);
        }
    };

    const handleEscapeChange = useCallback((checked) => {
        if (!input.trim()) {
            setIsEscaped(checked);
            return;
        }

        try {
            if (checked) {
                // User wants to Retain (Escape)
                let textToEscape = input;

                // Check if we can do a "Perfect Restore"
                // If the current content matches what we identified as "unescaped content" earlier,
                // and we have the "original escaped content" cached, restore it directly.
                if (restoreRef.current.original && restoreRef.current.unescaped === input) {
                    textToEscape = restoreRef.current.original;
                    // We treat this as the final escaped string, skipping re-escaping
                    setInput(textToEscape);
                    setIsEscaped(true);
                    setSuccessMsg('Escaped successfully (Restored)');
                    return;
                }

                // Standard fallback: Minify and Escape
                try {
                    const parsed = JSON.parse(input);
                    textToEscape = JSON.stringify(parsed);
                } catch (e) {
                    // If not valid JSON, escape as is
                }
                const escaped = escapeJson(textToEscape);
                setInput(escaped);
                setIsEscaped(true);
                setSuccessMsg('Escaped successfully');
            } else {
                // User wants to Remove (Unescape)
                try {
                    const unescaped = unescapeJson(input);
                    let displayValue = '';

                    if (typeof unescaped === 'object') {
                        displayValue = JSON.stringify(unescaped, null, 2);
                    } else {
                        displayValue = String(unescaped);
                    }

                    // Cache the values for potential restoration
                    restoreRef.current = {
                        original: input,
                        unescaped: displayValue
                    };

                    setInput(displayValue);
                    setIsEscaped(false);
                    setSuccessMsg('Unescaped successfully');
                } catch (e) {
                    setError('Cannot remove escape characters: Invalid format');
                    // 确保状态仍然更新，即使出错也要保持复选框状态一致
                    setIsEscaped(false);
                }
            }
        } catch (err) {
            setError('Operation failed: ' + err.message);
            // 确保状态仍然更新，即使出错也要保持复选框状态一致
            setIsEscaped(checked);
        }
    }, [input]);


    const handleClear = () => {
        setInput('');
        setError(null);
        setSuccessMsg('Cleared');
        setIsMinified(false); // 清空时重置压缩状态
        setIsFolded(false); // 清空时重置折叠状态
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(input);
            setSuccessMsg('Copied to clipboard');
        } catch (err) {
            setError('Failed to copy');
        }
    };

    const handleConvert = () => {
        try {
            const xml = jsonToXml(input);
            navigate('/xml', { state: { code: xml } });
        } catch (err) {
            setError('Conversion failed: ' + err.message);
        }
    };

    const handleFoldAll = () => {
        if (isFolded) {
            // 当前是折叠状态，执行展开
            if (editorRef.current?.unfoldAll) {
                editorRef.current.unfoldAll();
                setIsFolded(false);
                setSuccessMsg('All code unfolded');
            }
        } else {
            // 当前是展开状态，执行折叠
            if (editorRef.current?.foldAll) {
                editorRef.current.foldAll();
                setIsFolded(true);
                setSuccessMsg('All code folded');
            }
        }
    };

    return (
        <div className="workspace">
            <Toolbar
                onFormat={handleFormat}
                onMinify={handleMinify}
                onClear={handleClear}
                onCopy={handleCopy}
                onConvert={handleConvert}
                onEscapeChange={handleEscapeChange}
                onFoldAll={handleFoldAll}
                isEscaped={isEscaped}
                isMinified={isMinified}
                isFolded={isFolded}
                showEscapeCheckbox={true}
                escapeLabel={t('escapeLabel')}
                convertLabel={t('convertXml')}
                formatLabel={t('format')}
                minifyLabel={isMinified ? t('expand') : t('minify')}
                foldAllLabel={isFolded ? t('unfoldAll') : t('foldAll')}
                clearLabel={t('clear')}
                copyLabel={t('copy')}
            >
                <Link to="/" style={{
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginRight: '12px',
                    transition: 'opacity 0.2s'
                }}
                    onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                    title="Back to Home"
                >
                    <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: 'var(--accent-gradient)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 'var(--shadow-glow)'
                    }}>
                        <Code2 size={20} color="#fff" />
                    </div>
                </Link>
            </Toolbar>

            <div className="workspace-content">
                <div className="panel">
                    <SmartEditor
                        ref={editorRef}
                        value={input}
                        onChange={(code) => {
                            setInput(code);
                            if (error) setError(null);
                        }}
                        language="json"
                        uiLanguage={t('langCode')}
                    />
                </div>
            </div>

            <AnimatePresence>
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="status-bar error"
                    >
                        {error}
                    </motion.div>
                )}
                {successMsg && !error && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="status-bar success"
                    >
                        {successMsg}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default JsonWorkspace;
